import {
  Box,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import { Control, Controller } from "react-hook-form";
import StyledTextField from "@/modules/common/components/input/styled-text-field";
import CustomAutoComplete from "@/modules/common/components/input/dropdowns/auto-complete";
import { FormSection } from "@/modules/property-tracks/types";
import {
  FieldType,
  TrackStatusFormField,
} from "@/modules/property-tracks/dtos/property-stage";
import { convertCamelCaseToWords } from "@/modules/common/utils";
import { format } from "date-fns";
import DropDownFieldsRenderer from "./drop-down-fields-renderer";
import { useDynamicFieldOptions } from "@/modules/property-track-settings/hooks/get-dynamic-field-options.hook";

function isDynamicField(field: TrackStatusFormField): boolean {
  if (field.fieldType === FieldType.Dynamic) {
    return true;
  }
  const n = field.name.toLowerCase();
  return n.startsWith("dynamic") && !!field.dynamicFormUrl;
}

function toDateTimeInputValue(value: string) {
  if (!value) {
    return "";
  }
  const dateTime = new Date(value);
  if (Number.isNaN(dateTime.getTime())) {
    return "";
  }
  const p = (n: number) => String(n).padStart(2, "0");
  return `${dateTime.getFullYear()}-${p(dateTime.getMonth() + 1)}-${p(
    dateTime.getDate()
  )}T${p(dateTime.getHours())}:${p(dateTime.getMinutes())}`;
}

const DynamicFieldAutoComplete = ({
  field,
  section,
  control,
}: {
  field: TrackStatusFormField;
  section: FormSection;
  control: Control<any>;
}) => {
  const { options, loading } = useDynamicFieldOptions(field);
  const mappedOptions = options.map((o) => ({
    label: o.label,
    value: String(o.value),
  }));

  return (
    <Controller
      name={`${section.fieldLevel}.${field.name}`}
      control={control}
      render={({ field: controllerField, fieldState }) => (
        <CustomAutoComplete
          options={mappedOptions}
          multiple={false}
          fullWidth
          loading={loading}
          placeholder={convertCamelCaseToWords(field.name)}
          onChange={(value) => controllerField.onChange(value)}
          error={!!fieldState.error}
          helperText={fieldState.error?.message}
          value={controllerField.value ?? null}
        />
      )}
    />
  );
};

const StaticConfigurableDropDown = ({
  field,
  section,
  control,
}: {
  field: TrackStatusFormField;
  section: FormSection;
  control: Control<any>;
}) => {
  const options =
    field.fieldDropdowns?.map((row) => ({
      label: row.label ?? String(row.value ?? ""),
      value: String(row.value ?? ""),
    })) ?? [];

  return (
    <Controller
      name={`${section.fieldLevel}.${field.name}`}
      control={control}
      render={({ field: controllerField, fieldState }) => (
        <CustomAutoComplete
          options={options}
          multiple={false}
          fullWidth
          loading={false}
          placeholder={convertCamelCaseToWords(field.name)}
          onChange={(value) => controllerField.onChange(value)}
          error={!!fieldState.error}
          helperText={fieldState.error?.message}
          value={controllerField.value ?? null}
        />
      )}
    />
  );
};

const FieldRenderer = ({
  field,
  section,
  control,
}: {
  field: TrackStatusFormField;
  section: FormSection;
  control: Control<any>;
}) => {
  const specialFields = [
    "propertyTypeId",
    "compoundId",
    "finishing",
    "rentalTag",
    "hospitalityType",
    "scopeOfFinishing",
  ];

  if (specialFields.includes(field.name)) {
    return (
      <Stack gap={1} key={field.uid}>
        <Typography variant="body2" color="text.secondary">
          {convertCamelCaseToWords(field.name)}
        </Typography>
        <DropDownFieldsRenderer
          field={field}
          section={section}
          control={control}
        />
      </Stack>
    );
  }

  if (isDynamicField(field)) {
    return (
      <Stack key={field.uid} gap={1}>
        <Typography variant="body2" color="text.secondary">
          {convertCamelCaseToWords(field.name)}
        </Typography>
        <DynamicFieldAutoComplete
          field={field}
          section={section}
          control={control}
        />
      </Stack>
    );
  }

  if (field.isDropDown) {
    return (
      <Stack gap={1} key={field.uid}>
        <Typography variant="body2" color="text.secondary">
          {convertCamelCaseToWords(field.name)}
        </Typography>
        <StaticConfigurableDropDown
          field={field}
          section={section}
          control={control}
        />
      </Stack>
    );
  }

  return (
    <Box key={field.uid}>
      <Controller
        name={`${section.fieldLevel}.${field.name}`}
        control={control}
        render={({ field: controllerField, fieldState }) => {
          switch (field.fieldType) {
            case FieldType.String:
            case FieldType.Number:
              return (
                <StyledTextField
                  {...controllerField}
                  label={convertCamelCaseToWords(field.name)}
                  type={
                    field.fieldType === FieldType.Number ? "number" : "text"
                  }
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  fullWidth
                  size="small"
                />
              );
            case FieldType.Boolean:
              return (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={controllerField.value || false}
                      onChange={(e) =>
                        controllerField.onChange(e.target.checked)
                      }
                    />
                  }
                  label={convertCamelCaseToWords(field.name)}
                />
              );
            case FieldType.Date:
              return (
                <Stack gap={1}>
                  <Typography variant="body2" color="text.secondary">
                    {convertCamelCaseToWords(field.name)}
                  </Typography>
                  <StyledTextField
                    type="date"
                    fullWidth
                    size="small"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    value={
                      controllerField.value
                        ? format(new Date(controllerField.value), "yyyy-MM-dd")
                        : ""
                    }
                    onChange={(e) =>
                      controllerField.onChange(
                        e.target.value
                          ? new Date(e.target.value).toISOString()
                          : undefined
                      )
                    }
                  />
                </Stack>
              );
            case FieldType.DateTime:
              return (
                <Stack gap={1}>
                  <Typography variant="body2" color="text.secondary">
                    {convertCamelCaseToWords(field.name)}
                  </Typography>
                  <StyledTextField
                    type="datetime-local"
                    fullWidth
                    size="small"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    slotProps={{
                      inputLabel: {
                        shrink: true,
                      },
                    }}
                    value={toDateTimeInputValue(controllerField.value)}
                    onChange={(e) =>
                      controllerField.onChange(
                        e.target.value
                          ? new Date(e.target.value).toISOString()
                          : undefined
                      )
                    }
                  />
                </Stack>
              );
            case FieldType.Time:
              return (
                <Stack gap={1}>
                  <Typography variant="body2" color="text.secondary">
                    {convertCamelCaseToWords(field.name)}
                  </Typography>
                  <StyledTextField
                    type="time"
                    fullWidth
                    size="small"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    slotProps={{
                      inputLabel: {
                        shrink: true,
                      },
                    }}
                    value={controllerField.value ?? ""}
                    onChange={(e) => controllerField.onChange(e.target.value)}
                  />
                </Stack>
              );
            default:
              return <></>;
          }
        }}
      />
    </Box>
  );
};

export default FieldRenderer;
